import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { useMasks } from "@/hooks/useMasks";
import { Mask } from "@/types/mask";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useArchiveTestLabel, useDeleteTestLabel, useTestLabels } from "@/hooks/useTestLabels";
import DeleteActionDialog from "@/components/shared/DeleteActionDialog";
import { TestLabel } from "@/types/testLabel";
import { TestLabelFilter } from "./TestLabelFilter";
import TestLabelDataGrid from "./TestLabelDataGrid";
import { GenericErrorDisplay } from "../shared/ErrorDisplay";
import { LoadingDisplay } from "../shared/LoadingDisplay";
import { Main } from "../shared/Main";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";

export default function TestLabelOverview() {
  const navigate = useNavigate();
  const { data: masks, error: isMasksError, isLoading: isMasksLoading } = useMasks();
  const {
    data: testLabels,
    error: isTestLabelsError,
    isLoading: isTestLabelsLoading,
    mutate: refetchTestLabels,
  } = useTestLabels();

  const [selectedMask, setSelectedMask] = useState<Mask | undefined>(undefined);
  const [testLabelToDelete, setTestLabelToDelete] = useState<TestLabel | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showArchived, setShowArchived] = useState(false);

  const { trigger: deleteTestLabel, isMutating: isExecutingDeletion } = useDeleteTestLabel();
  const { trigger: archiveTestLabel } = useArchiveTestLabel();

  const labelRows = useMemo(() => {
    const filtered = testLabels.filter((tl) =>
      selectedMask ? tl.mask.id === selectedMask.id : true,
    );

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return filtered.filter(
        (tl) =>
          tl.name.toLowerCase().includes(query) ||
          tl.description?.toLowerCase().includes(query) ||
          tl.mask?.name.toLowerCase().includes(query),
      );
    }

    return filtered;
  }, [selectedMask, testLabels, searchQuery]);

  const onEditTestLabel = (testLabelId: number) => {
    navigate(`/test-labels/${testLabelId}/edit`);
  };

  const handleDelete = async (testLabel: TestLabel) => {
    setTestLabelToDelete(testLabel);
  };

  const onDeleteLabel = async () => {
    try {
      if (!testLabelToDelete) return;

      await deleteTestLabel(
        {
          id: testLabelToDelete.id,
        },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: () => {
            toast.success(`Successfully deleted the Test Label: "${testLabelToDelete.name}"!`);
            setTestLabelToDelete(null);
            refetchTestLabels();
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleArchiveToggle = async (testLabel: TestLabel) => {
    try {
      const successMessage = testLabel.isArchived
        ? `Successfully unarchived Test Label ${testLabel.name}.`
        : `Successfully archived Test Label ${testLabel.name}!`;

      await archiveTestLabel(
        {
          id: testLabel.id,
        },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: () => {
            toast.success(successMessage);
            refetchTestLabels();
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const onMaskChange = (maskId: string) => {
    const filteredMask = masks.find((m) => m.id === maskId);
    setSelectedMask(filteredMask);
  };

  if (isMasksLoading || isTestLabelsLoading) {
    return <LoadingDisplay />;
  }

  if (isMasksError || isTestLabelsError) {
    return <GenericErrorDisplay />;
  }

  const filteredRows = labelRows.filter((label) => (showArchived ? true : !label.isArchived));

  return (
    <Main className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Test Labels</h1>
          <p className="text-muted-foreground">View and manage all Test Labels</p>
        </div>
      </div>
      <TestLabelFilter
        masks={masks}
        selectedMask={selectedMask}
        onMaskChange={onMaskChange}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>
              Test Labels
              {filteredRows.length > 0 && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  ({filteredRows.length} total)
                </span>
              )}
            </span>
            <div className="flex items-center space-x-2">
              <Switch id="show-archived" checked={showArchived} onCheckedChange={setShowArchived} />
              <Label htmlFor="show-archived" className="text-sm font-medium">
                Show Archived
              </Label>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <TestLabelDataGrid
            labels={filteredRows}
            onEdit={onEditTestLabel}
            onDelete={handleDelete}
            onArchive={handleArchiveToggle}
          />
        </CardContent>
      </Card>

      {testLabelToDelete ? (
        <DeleteActionDialog
          action={onDeleteLabel}
          description={`Are you sure you want to delete Test Label "${testLabelToDelete.name}"?`}
          isExecutingAction={isExecutingDeletion}
          open
          onOpenChange={() => setTestLabelToDelete(null)}
          title="Delete Test Label"
        />
      ) : null}
    </Main>
  );
}
